/**
 * Shared formatting utilities
 */

/**
 * Parse Firestore Timestamp to Date object with defensive type checking
 *
 * Handles multiple timestamp formats from Firestore:
 * - Firestore Timestamp objects with toDate() method (unserialized)
 * - Plain objects with seconds property (serialized)
 * - null/undefined values
 *
 * @param value - Unknown value that may be a Firestore Timestamp
 * @returns Date object if successfully parsed, null otherwise
 */
export const parseTimestamp = (value: unknown): Date | null => {
  // Handle null/undefined
  if (value == null) {
    return null;
  }

  // Type guard: Check if value is an object
  if (typeof value !== 'object') {
    return null;
  }

  // Check for Firestore Timestamp with toDate method (unserialized)
  if ('toDate' in value && typeof value.toDate === 'function') {
    try {
      return value.toDate() as Date;
    } catch {
      return null;
    }
  }

  // Check for plain object with seconds property (serialized)
  if ('seconds' in value && typeof value.seconds === 'number') {
    // Validate seconds is a valid number (not NaN or Infinity)
    if (!Number.isFinite(value.seconds)) {
      return null;
    }
    return new Date(value.seconds * 1000);
  }

  // Invalid format
  return null;
};

/**
 * Format date for display with consistent format across the application
 * @param dateString ISO 8601 date string
 * @returns Formatted date string or dash if no date provided
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
